import { useCallback, useRef } from 'react';
import { FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS } from 'common/constants/allocations';
import { EvaluatedCell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  UseWaterfallInputHandlersParams,
  UseWaterfallInputHandlersResult,
} from './useWaterfallInputHandlers.props';
import {
  CAP_TABLE_ALIAS,
  COMPANY_ALIAS,
  EXIT_DATE_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  LESS_DEBT_ALIAS,
  MEASUREMENT_DATE_ALIAS,
  PLUS_CASH_ALIAS,
} from '../../SpreadsheetConfig/constants';

export type { UseWaterfallInputHandlersParams, UseWaterfallInputHandlersResult } from './useWaterfallInputHandlers.props';

export const useWaterfallInputHandlers = ({
  urlState,
  onChange,
  workbookCells,
  setScenarios,
  resetChildrenSheets,
  capTableList,
  childrenCapTables,
  companyMeasurementDates,
}: UseWaterfallInputHandlersParams): UseWaterfallInputHandlersResult => {
  // Refs to avoid stale closure issues in handlers passed to spreadsheet components
  const companyMeasurementDatesRef = useRef(companyMeasurementDates);
  const capTableListRef = useRef(capTableList);
  const childrenCapTablesRef = useRef(childrenCapTables);

  // Keep refs updated
  companyMeasurementDatesRef.current = companyMeasurementDates;
  capTableListRef.current = capTableList;
  childrenCapTablesRef.current = childrenCapTables;

  // ============================================================
  // Individual Handlers
  // ============================================================
  const handleExitDateChange = useCallback(
    (value: any) => {
      urlState.setExitDate(value as string);
    },
    [urlState]
  );

  const handleEquityValueChange = useCallback(
    (value: any) => {
      urlState.setEquityValue(value as number);
    },
    [urlState]
  );

  const handleEnterpriseValueChange = useCallback(
    (value: any) => {
      urlState.setEnterpriseValue(value as number);
    },
    [urlState]
  );

  const handleCashChange = useCallback(
    (value: any) => {
      const num = value === '' || value === null || value === undefined ? null : (value as number);
      urlState.setCash(num);
    },
    [urlState]
  );

  const handleDebtChange = useCallback(
    (value: any) => {
      const num = value === '' || value === null || value === undefined ? null : (value as number);
      urlState.setDebt(num);
    },
    [urlState]
  );

  const handleCapTableChange = useCallback(
    (value: any) => {
      // Use refs to avoid stale closure - spreadsheet components may cache the callback
      const currentCapTableList = capTableListRef.current;
      const currentChildrenCapTables = childrenCapTablesRef.current;
      // Use toString comparison to handle both string and number IDs
      const valueStr = String(value);
      // Look in both main cap table list and children cap tables
      const captable = currentCapTableList.find(ct => String(ct.id) === valueStr)
        || currentChildrenCapTables.find(ct => String(ct.id) === valueStr);
      if (captable) {
        urlState.setCaptable(captable);
        setScenarios(undefined);
        resetChildrenSheets();
      }
    },
    [urlState, setScenarios, resetChildrenSheets]
  );

  const handleCompanyChange = useCallback(
    (value: any) => {
      const newCompanyId = Number(value);
      urlState.resetForCompanyChange(newCompanyId);
      setScenarios(undefined);
      resetChildrenSheets();
    },
    [urlState, setScenarios, resetChildrenSheets]
  );

  const handleMeasurementDateChange = useCallback(
    (value: any) => {
      // Use ref to avoid stale closure - spreadsheet components may cache the callback
      const currentMeasurementDates = companyMeasurementDatesRef.current;
      // Use toString comparison to handle both string and number IDs
      const valueStr = String(value);
      const newMeasurementDate = currentMeasurementDates.find(md => String(md.id) === valueStr);
      if (newMeasurementDate) {
        // Set new measurement date and clear cap table so it defaults to primary for new measurement date
        urlState.setMeasurementDate(newMeasurementDate);
        urlState.clearCaptable();
        setScenarios(undefined);
      }
    },
    [urlState, setScenarios]
  );

  // ============================================================
  // Fund-specific handlers (read from cells and delegate)
  // ============================================================
  const handleFundExitEnterpriseValueChange = useCallback(() => {
    const enterpriseValueCell = Object.values(workbookCells?.InputSheet ?? {}).find(
      c => c.alias === EXIT_ENTERPRISE_VALUE_ALIAS
    );
    if (enterpriseValueCell) {
      handleEnterpriseValueChange(enterpriseValueCell.value);
    }
  }, [handleEnterpriseValueChange, workbookCells]);

  const handleFundExitEquityValueChange = useCallback(() => {
    const equityValueCell = Object.values(workbookCells?.InputSheet ?? {}).find(c => c.alias === EXIT_EQUITY_ALIAS);
    if (equityValueCell) {
      handleEquityValueChange(equityValueCell.value);
    }
  }, [handleEquityValueChange, workbookCells]);

  // ============================================================
  // Main Workbook Change Handler
  // ============================================================
  const onWorkbookChange = useCallback(
    (cell: EvaluatedCell, value: any) => {
      onChange(cell, value);
      switch (cell.alias) {
        case EXIT_DATE_ALIAS:
          handleExitDateChange(value);
          break;
        case EXIT_EQUITY_ALIAS:
          handleEquityValueChange(value);
          break;
        case FUND_EXIT_ENTERPRISE_ALIAS:
          handleFundExitEnterpriseValueChange();
          break;
        case FUND_EXIT_EQUITY_ALIAS:
          handleFundExitEquityValueChange();
          break;
        case EXIT_ENTERPRISE_VALUE_ALIAS:
          handleEnterpriseValueChange(value);
          break;
        case PLUS_CASH_ALIAS:
          handleCashChange(value);
          break;
        case LESS_DEBT_ALIAS:
          handleDebtChange(value);
          break;
        case CAP_TABLE_ALIAS:
          handleCapTableChange(value);
          break;
        case COMPANY_ALIAS:
          handleCompanyChange(value);
          break;
        case MEASUREMENT_DATE_ALIAS:
          handleMeasurementDateChange(value);
          break;
        default:
          break;
      }
    },
    [
      onChange,
      handleExitDateChange,
      handleEquityValueChange,
      handleEnterpriseValueChange,
      handleCapTableChange,
      handleCompanyChange,
      handleMeasurementDateChange,
      handleFundExitEnterpriseValueChange,
      handleFundExitEquityValueChange,
      handleCashChange,
      handleDebtChange,
    ]
  );

  return {
    onWorkbookChange,
    handleExitDateChange,
    handleEquityValueChange,
    handleEnterpriseValueChange,
    handleCashChange,
    handleDebtChange,
    handleCapTableChange,
    handleCompanyChange,
    handleMeasurementDateChange,
  };
};
